import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  Activity,
  BrainCircuit,
  LayoutGrid,
  QrCode,
  Rocket,
  Sparkles,
  TrendingUp,
  Users,
} from "lucide-react";
import { Orb } from "@/components/Orb";
import { WTCLogo } from "@/components/WTCLogo";
import { QRBlock } from "@/components/QRBlock";
import { getStats, listBusinesses, listTeammates } from "@/lib/api";
import type { BusinessSummary, Stats, TeammateSummary } from "@/lib/types";
import { cn } from "@/lib/cn";

type TabId = "qr" | "feed" | "orb" | "marketplace" | "stats";
type Experience = "build" | "reimagine";

const TABS: { id: TabId; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { id: "qr", label: "Scan", icon: QrCode },
  { id: "feed", label: "Live feed", icon: Activity },
  { id: "orb", label: "Presence", icon: Sparkles },
  { id: "marketplace", label: "Marketplace", icon: LayoutGrid },
  { id: "stats", label: "Results", icon: TrendingUp },
];

export default function Stage() {
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState<TabId>((params.get("tab") as TabId) || "qr");
  const [exp, setExp] = useState<Experience>(
    params.get("exp") === "reimagine" ? "reimagine" : "build",
  );

  const [stats, setStats] = useState<Stats | null>(null);
  const [teammates, setTeammates] = useState<TeammateSummary[]>([]);
  const [businesses, setBusinesses] = useState<BusinessSummary[]>([]);
  const [pulseKey, setPulseKey] = useState(0);
  const lastCountRef = useRef(0);

  const refresh = useCallback(async () => {
    try {
      if (exp === "build") {
        const [s, list] = await Promise.all([getStats("all"), listTeammates({ limit: 60 })]);
        setStats(s);
        setTeammates(list);
        if (lastCountRef.current && s.total > lastCountRef.current) {
          setPulseKey((k) => k + 1);
        }
        lastCountRef.current = s.total;
      } else {
        const list = await listBusinesses({ limit: 60 });
        setBusinesses(list);
        if (lastCountRef.current && list.length > lastCountRef.current) {
          setPulseKey((k) => k + 1);
        }
        lastCountRef.current = list.length;
      }
    } catch {
      /* keep last state */
    }
  }, [exp]);

  useEffect(() => {
    lastCountRef.current = 0;
    refresh();
    const t = setInterval(refresh, 2500);
    return () => clearInterval(t);
  }, [refresh]);

  useEffect(() => {
    const next = new URLSearchParams(params);
    next.set("tab", tab);
    next.set("exp", exp);
    setParams(next, { replace: true });
  }, [tab, exp, params, setParams]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === "1") setTab("qr");
      else if (e.key === "2") setTab("feed");
      else if (e.key === "3") setTab("orb");
      else if (e.key === "4") setTab("marketplace");
      else if (e.key === "5") setTab("stats");
      else if (e.key.toLowerCase() === "e") {
        setExp((prev) => (prev === "build" ? "reimagine" : "build"));
      } else if (e.key.toLowerCase() === "f") {
        if (document.fullscreenElement) document.exitFullscreen();
        else document.documentElement.requestFullscreen();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const origin = typeof window !== "undefined" ? window.location.origin : "https://wtc.ruicosta.ai";
  const qrUrl = `${origin}/?go=${exp}`;
  const total = exp === "build" ? stats?.total ?? 0 : businesses.length;

  return (
    <div className="relative min-h-dvh overflow-hidden">
      <AmbientBackground pulseKey={pulseKey} />
      <div className="relative z-10 mx-auto flex min-h-dvh max-w-[1440px] flex-col px-10 py-8">
        <TopBar total={total} exp={exp} onExpChange={setExp} pulseKey={pulseKey} />

        <div className="mt-8 flex flex-1 items-stretch">
          <AnimatePresence mode="wait">
            {tab === "qr" && <QRTab key={`qr-${exp}`} url={qrUrl} exp={exp} />}
            {tab === "feed" && (
              <FeedTab key={`feed-${exp}`} exp={exp} teammates={teammates} businesses={businesses} />
            )}
            {tab === "orb" && (
              <OrbTab key="orb" pulseKey={pulseKey} total={total} exp={exp} />
            )}
            {tab === "marketplace" && (
              <MarketplaceTab
                key={`mkt-${exp}`}
                exp={exp}
                teammates={teammates}
                businesses={businesses}
              />
            )}
            {tab === "stats" && (
              <StatsTab key={`stats-${exp}`} exp={exp} stats={stats} businesses={businesses} />
            )}
          </AnimatePresence>
        </div>

        <TabBar active={tab} onChange={setTab} />
      </div>
    </div>
  );
}

function AmbientBackground({ pulseKey }: { pulseKey: number }) {
  return (
    <>
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(1200px 900px at 85% -20%, rgba(94,234,255,0.14), transparent 60%),radial-gradient(1100px 800px at 15% 120%, rgba(28,127,255,0.22), transparent 60%)",
        }}
      />
      <AnimatePresence>
        <motion.div
          key={pulseKey}
          className="pointer-events-none absolute inset-0"
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 0.45, 0] }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.8, ease: "easeOut" }}
          style={{
            background:
              "radial-gradient(800px 600px at 50% 50%, rgba(94,234,255,0.35), transparent 70%)",
          }}
        />
      </AnimatePresence>
    </>
  );
}

function TopBar({
  total,
  exp,
  onExpChange,
  pulseKey,
}: {
  total: number;
  exp: Experience;
  onExpChange: (e: Experience) => void;
  pulseKey: number;
}) {
  const titles =
    exp === "build"
      ? { kicker: "Session 01 · You can build the future today", title: "AI Teammate Builder" }
      : { kicker: "Session 02 · You can build the future today", title: "Reimagine Your Business" };

  return (
    <div className="flex items-center gap-6">
      <div className="flex items-center gap-4">
        <WTCLogo className="h-8" />
        <div className="h-8 w-px bg-white/10" />
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-white/50">
            {titles.kicker}
          </div>
          <div className="text-2xl font-semibold leading-none text-white">{titles.title}</div>
        </div>
      </div>

      <div className="ml-auto flex items-center gap-3">
        <motion.div
          key={pulseKey + "-counter"}
          initial={{ scale: 1 }}
          animate={{ scale: [1, 1.08, 1] }}
          transition={{ duration: 0.6 }}
          className="glass glow-border flex items-center gap-3 rounded-full px-5 py-2"
        >
          <Users className="h-4 w-4 text-cyan-glow" />
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/50">
              {exp === "build" ? "Teammates" : "Businesses"}
            </div>
            <div className="text-2xl font-semibold leading-none text-white">{total}</div>
          </div>
        </motion.div>

        <div className="glass flex items-center gap-1 rounded-full p-1">
          <ExpButton active={exp === "build"} onClick={() => onExpChange("build")} icon={BrainCircuit}>
            Build
          </ExpButton>
          <ExpButton active={exp === "reimagine"} onClick={() => onExpChange("reimagine")} icon={Rocket}>
            Reimagine
          </ExpButton>
        </div>
      </div>
    </div>
  );
}

function ExpButton({
  active,
  onClick,
  icon: Icon,
  children,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-medium transition",
        active ? "bg-white text-ink-900" : "text-white/60 hover:text-white",
      )}
    >
      <Icon className="h-3.5 w-3.5" />
      {children}
    </button>
  );
}

function TabBar({ active, onChange }: { active: TabId; onChange: (t: TabId) => void }) {
  return (
    <div className="mt-8 flex justify-center">
      <div className="glass flex items-center gap-1 rounded-full p-1.5">
        {TABS.map(({ id, label, icon: Icon }, idx) => (
          <button
            key={id}
            onClick={() => onChange(id)}
            className={cn(
              "flex items-center gap-2 rounded-full px-5 py-2.5 text-sm transition",
              active === id ? "bg-white text-ink-900" : "text-white/60 hover:text-white",
            )}
          >
            <Icon className="h-4 w-4" />
            {label}
            <span
              className={cn(
                "ml-1 rounded px-1.5 py-0.5 text-[10px]",
                active === id ? "bg-ink-900/10 text-ink-900/50" : "bg-white/5 text-white/40",
              )}
            >
              {idx + 1}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

function QRTab({ url, exp }: { url: string; exp: Experience }) {
  const copy =
    exp === "build"
      ? {
          tag: "Session 01 · 3 minutes",
          headlinePre: "Build your",
          headlineAccent: "AI teammate",
          sub: "Take 3 minutes. Scan the QR. Design an AI that takes the first 80% off one task in your job.",
          bullets: [
            "Pick one repetitive task",
            "Name your AI teammate",
            "Leave with a prompt you can use Monday morning",
          ],
        }
      : {
          tag: "Session 02 · 5 minutes",
          headlinePre: "Reimagine your",
          headlineAccent: "business",
          sub: "Three sentences in. Three bold AI moves out. One you'll act on this month.",
          bullets: [
            "Describe your business in 3 sentences",
            "Get one move to AUTOMATE, one to LAUNCH, one to EXPAND",
            "Commit to the one you'll act on this month",
          ],
        };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="grid w-full grid-cols-1 items-center gap-12 lg:grid-cols-2"
    >
      <div className="flex items-center justify-center">
        <QRBlock url={url} size={360} label="Scan with your phone camera" />
      </div>
      <div>
        <div className="inline-flex items-center gap-1.5 rounded-md border border-white/10 bg-white/5 px-3 py-1 text-xs uppercase tracking-widest text-white/60">
          {copy.tag}
        </div>
        <h1 className="mt-5 text-7xl font-semibold leading-[0.95] text-white">
          {copy.headlinePre}
          <br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-glow to-brand-400">
            {copy.headlineAccent}
          </span>
        </h1>
        <p className="mt-6 max-w-lg text-xl leading-relaxed text-white/70">{copy.sub}</p>
        <ul className="mt-6 space-y-2 text-base text-white/80">
          {copy.bullets.map((b, i) => (
            <li key={i} className="flex items-center gap-2">
              <span className="text-cyan-glow">•</span> {b}
            </li>
          ))}
        </ul>
      </div>
    </motion.div>
  );
}

function FeedTab({
  exp,
  teammates,
  businesses,
}: {
  exp: Experience;
  teammates: TeammateSummary[];
  businesses: BusinessSummary[];
}) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      className="w-full"
    >
      <div className="mb-4 flex items-center gap-3">
        <div className="flex items-center gap-1.5 rounded-full bg-cyan-glow/10 px-3 py-1 text-xs text-cyan-glow">
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-cyan-glow/80" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-cyan-glow" />
          </span>
          live feed
        </div>
        <div className="text-sm text-white/40">
          {exp === "build" ? "Latest AI teammates created right now" : "Latest businesses being reimagined"}
        </div>
      </div>

      <div className="no-scrollbar max-h-[64vh] overflow-y-auto pr-2">
        {exp === "build" ? (
          <TeammateGrid teammates={teammates} />
        ) : (
          <BusinessGrid businesses={businesses} />
        )}
      </div>
    </motion.div>
  );
}

function TeammateGrid({ teammates }: { teammates: TeammateSummary[] }) {
  if (teammates.length === 0) return <EmptyState label="Waiting for the first teammate…" />;
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
      <AnimatePresence initial={false}>
        {teammates.map((t) => (
          <motion.a
            key={t.id}
            href={`/t/${t.id}`}
            target="_blank"
            rel="noopener noreferrer"
            layout
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            whileHover={{ y: -3, scale: 1.01 }}
            className="glass block rounded-lg p-5 transition-colors hover:bg-white/[0.06]"
            style={{ borderColor: `${t.accent}33` }}
          >
            <div className="flex items-start gap-3">
              <div
                className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg text-2xl"
                style={{ background: `${t.accent}22` }}
              >
                {t.emoji}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-lg font-semibold text-white truncate">{t.name}</div>
                <div className="text-xs text-white/50 capitalize">
                  {t.function} · {t.role}
                  {t.creatorName && (
                    <span className="ml-1 text-white/40">· by {t.creatorName}</span>
                  )}
                </div>
              </div>
            </div>
            <p className="mt-3 line-clamp-3 text-sm leading-snug text-white/75">{t.mission}</p>
          </motion.a>
        ))}
      </AnimatePresence>
    </div>
  );
}

function BusinessGrid({ businesses }: { businesses: BusinessSummary[] }) {
  if (businesses.length === 0) return <EmptyState label="Waiting for the first reimagined business…" />;
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
      <AnimatePresence initial={false}>
        {businesses.map((b) => (
          <motion.a
            key={b.id}
            href={`/b/${b.id}`}
            target="_blank"
            rel="noopener noreferrer"
            layout
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            whileHover={{ y: -3, scale: 1.01 }}
            className="glass block rounded-lg p-5 transition-colors hover:bg-white/[0.06]"
            style={{ borderColor: `${b.accent}33` }}
          >
            <div className="flex items-start gap-3">
              <div
                className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg text-2xl"
                style={{ background: `${b.accent}22` }}
              >
                {b.emoji}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-base font-semibold text-white truncate">
                  {b.businessName || b.industry}
                </div>
                <div className="text-xs text-white/50 truncate">
                  {b.industry}
                  {b.creatorName && (
                    <span className="ml-1 text-white/40">· by {b.creatorName}</span>
                  )}
                </div>
              </div>
              {b.commitment && (
                <span
                  className="rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide"
                  style={{ background: `${b.accent}33`, color: "white" }}
                >
                  {b.commitment}
                </span>
              )}
            </div>
            <p className="mt-3 line-clamp-3 text-sm leading-snug text-white/85">{b.headline}</p>
          </motion.a>
        ))}
      </AnimatePresence>
    </div>
  );
}

function OrbTab({
  pulseKey,
  total,
  exp,
}: {
  pulseKey: number;
  total: number;
  exp: Experience;
}) {
  const [intensity, setIntensity] = useState<"idle" | "pulse">("idle");
  useEffect(() => {
    if (!pulseKey) return;
    setIntensity("pulse");
    const t = setTimeout(() => setIntensity("idle"), 2000);
    return () => clearTimeout(t);
  }, [pulseKey]);

  const word = exp === "build" ? "minds building" : "businesses being reimagined";

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="flex w-full flex-col items-center justify-center gap-10 text-center"
    >
      <Orb size={520} intensity={intensity} />
      <div>
        <div className="text-sm uppercase tracking-[0.3em] text-white/40">WTC Lisboa · live</div>
        <h2 className="mt-3 text-6xl font-semibold leading-tight text-white">
          <span className="text-cyan-glow">{total}</span> {word}
        </h2>
        <p className="mt-4 max-w-xl text-lg text-white/60">
          Every pulse is something being born in this room.
        </p>
      </div>
    </motion.div>
  );
}

function MarketplaceTab({
  exp,
  teammates,
  businesses,
}: {
  exp: Experience;
  teammates: TeammateSummary[];
  businesses: BusinessSummary[];
}) {
  const title = exp === "build" ? "AI Teammate Marketplace" : "Bold-moves Marketplace";
  const sub =
    exp === "build"
      ? "Every teammate built today — browse, borrow, remix."
      : "Every reimagined business — see how others are bending AI to their advantage.";
  const count = exp === "build" ? teammates.length : businesses.length;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      className="w-full"
    >
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-semibold text-white">{title}</h2>
          <p className="text-sm text-white/60">{sub}</p>
        </div>
        <div className="text-xs text-white/40">{count} shown</div>
      </div>
      <div className="no-scrollbar max-h-[62vh] overflow-y-auto pr-2">
        {exp === "build" ? (
          <TeammateGrid teammates={teammates} />
        ) : (
          <BusinessGrid businesses={businesses} />
        )}
      </div>
    </motion.div>
  );
}

function StatsTab({
  exp,
  stats,
  businesses,
}: {
  exp: Experience;
  stats: Stats | null;
  businesses: BusinessSummary[];
}) {
  if (exp === "build") return <BuildStatsTab stats={stats} />;
  return <ReimagineStatsTab businesses={businesses} />;
}

function BuildStatsTab({ stats }: { stats: Stats | null }) {
  const functionsSorted = useMemo(() => {
    if (!stats) return [];
    return Object.entries(stats.byFunction)
      .filter(([, n]) => n > 0)
      .sort((a, b) => b[1] - a[1]);
  }, [stats]);
  const max = functionsSorted[0]?.[1] ?? 1;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      className="w-full"
    >
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Stat label="Teammates built" value={stats?.total ?? 0} highlight />
        <Stat label="Distinct names" value={stats?.topNames?.length ?? 0} />
        <Stat label="Roles represented" value={stats?.topRoles?.length ?? 0} />
      </div>
      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <div className="glass rounded-xl p-6">
          <h3 className="text-sm uppercase tracking-widest text-white/60">By function</h3>
          <div className="mt-4 space-y-3">
            {functionsSorted.length === 0 && (
              <div className="text-sm text-white/40">Waiting for data…</div>
            )}
            {functionsSorted.map(([f, n]) => (
              <div key={f}>
                <div className="flex items-baseline justify-between text-sm">
                  <span className="capitalize text-white/80">{f}</span>
                  <span className="font-mono text-white/60">{n}</span>
                </div>
                <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-white/5">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${(n / max) * 100}%` }}
                    transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                    className="h-full rounded-full bg-gradient-to-r from-cyan-glow to-brand-500"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="glass rounded-xl p-6">
          <h3 className="text-sm uppercase tracking-widest text-white/60">Popular names</h3>
          <div className="mt-3 flex flex-wrap gap-2">
            {(stats?.topNames ?? []).slice(0, 16).map((n) => (
              <span
                key={n.name}
                className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-white/80"
                style={{ fontSize: `${Math.min(22, 11 + n.count * 2)}px` }}
              >
                {n.name}
              </span>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function ReimagineStatsTab({ businesses }: { businesses: BusinessSummary[] }) {
  const total = businesses.length;
  const commitments = useMemo(() => {
    const c = { automate: 0, launch: 0, expand: 0 };
    for (const b of businesses) {
      if (b.commitment === "automate") c.automate++;
      else if (b.commitment === "launch") c.launch++;
      else if (b.commitment === "expand") c.expand++;
    }
    return c;
  }, [businesses]);
  const committedTotal = commitments.automate + commitments.launch + commitments.expand;

  const industries = useMemo(() => {
    const m = new Map<string, number>();
    for (const b of businesses) {
      const k = (b.industry || "Other").toLowerCase();
      m.set(k, (m.get(k) || 0) + 1);
    }
    return Array.from(m.entries()).sort((a, b) => b[1] - a[1]);
  }, [businesses]);
  const indMax = industries[0]?.[1] ?? 1;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      className="w-full"
    >
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Stat label="Businesses reimagined" value={total} highlight />
        <Stat label="People committed" value={committedTotal} />
        <Stat label="Distinct industries" value={industries.length} />
      </div>
      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <div className="glass rounded-xl p-6">
          <h3 className="text-sm uppercase tracking-widest text-white/60">Where they're betting</h3>
          <div className="mt-4 space-y-3">
            {(["automate", "launch", "expand"] as const).map((k) => {
              const n = commitments[k];
              const pct = committedTotal ? Math.round((n / committedTotal) * 100) : 0;
              const color =
                k === "automate" ? "#5EEAFF" : k === "launch" ? "#FFC36A" : "#A4F36F";
              return (
                <div key={k}>
                  <div className="flex items-baseline justify-between text-sm">
                    <span className="capitalize text-white/80">{k}</span>
                    <span className="font-mono text-white/60">
                      {n} ({pct}%)
                    </span>
                  </div>
                  <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-white/5">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${pct}%` }}
                      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                      className="h-full rounded-full"
                      style={{ background: color }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="glass rounded-xl p-6">
          <h3 className="text-sm uppercase tracking-widest text-white/60">Top industries</h3>
          <div className="mt-4 space-y-3">
            {industries.length === 0 && (
              <div className="text-sm text-white/40">Waiting for data…</div>
            )}
            {industries.slice(0, 8).map(([ind, n]) => (
              <div key={ind}>
                <div className="flex items-baseline justify-between text-sm">
                  <span className="capitalize text-white/80">{ind}</span>
                  <span className="font-mono text-white/60">{n}</span>
                </div>
                <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-white/5">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${(n / indMax) * 100}%` }}
                    transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                    className="h-full rounded-full bg-gradient-to-r from-cyan-glow to-brand-500"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function Stat({
  label,
  value,
  highlight,
}: {
  label: string;
  value: number;
  highlight?: boolean;
}) {
  return (
    <div className={cn("glass rounded-xl p-6", highlight && "glow-border")}>
      <div className="text-xs uppercase tracking-widest text-white/50">{label}</div>
      <div className="mt-2 text-6xl font-semibold leading-none text-white">{value}</div>
    </div>
  );
}

function EmptyState({ label }: { label: string }) {
  return (
    <div className="flex h-64 flex-col items-center justify-center text-center text-white/40">
      <Orb size={100} intensity="idle" />
      <p className="mt-6 text-sm">{label}</p>
    </div>
  );
}
